/* ── Centralized Motion & Animation Tokens ── */

/**
 * Shared transition presets.
 * Use these strings in style={{ transition: transitions.normal }} or
 * inside <style> blocks via template literals.
 */
export const transitions = {
  /** 150ms ease — micro-interactions (hover, focus ring) */
  fast: '150ms ease',
  /** 250ms ease — standard state changes */
  normal: '250ms ease',
  /** Alias for normal — use for standard transitions */
  standard: '250ms ease',
  /** 350ms ease — panel entrances, large moves */
  slow: '350ms ease',
  /** 250ms with overshoot — spring-like feel for toggles / scale */
  spring: '250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** 300ms ease-in-out — slide / drawer motions */
  slide: '300ms ease-in-out',
  /** 200ms ease-out — fade-only transitions */
  fade: '200ms ease-out',
} as const;

/**
 * Keyframe animation durations (CSS `animation-duration`).
 */
export const durations = {
  spin: '600ms',
  pulse: '1.5s',
  slide: '300ms',
  fade: '200ms',
  shimmer: '1.8s',
} as const;

/**
 * Easing presets for direct use in animation / transition properties.
 */
export const easings = {
  out: 'ease-out',
  inOut: 'ease-in-out',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  bounce: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
} as const;

/**
 * Helper: builds a `@media (prefers-reduced-motion: reduce)` + `[data-reduced-motion="true"]` CSS rule
 * that strips transitions/animations from the given selectors.
 *
 * Usage inside <style>:
 *   reducedMotionCSS('.ds-card', '.ds-card *')
 *   // → "@media (prefers-reduced-motion: reduce) { ... } [data-reduced-motion=true] .ds-card, [data-reduced-motion=true] .ds-card * { ... }"
 */
export function reducedMotionCSS(...selectors: string[]): string {
  const group = selectors.join(',\n');
  const dataAttrSelectors = selectors.map((s) => `[data-reduced-motion="true"] ${s}`).join(',\n');
  return `@media (prefers-reduced-motion: reduce) {\n  ${group} {\n    transition: none !important;\n    animation: none !important;\n    transform: none !important;\n  }\n}\n${dataAttrSelectors} {\n  transition: none !important;\n  animation: none !important;\n  transform: none !important;\n}`;
}
